import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Images, Loader2, Trash2 } from "lucide-react";

export const photoSrc = (p) => `${process.env.REACT_APP_BACKEND_URL}${p.url}`;

export function PhotoStrip({ photos = [], onDeleted, canDelete, testPrefix = "photo" }) {
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  const del = async (p) => {
    if (!window.confirm("Smazat fotku?")) return;
    try { await api.delete(`/photos/${p.id}`); toast.success("Fotka smazána"); setOpen(null); onDeleted?.(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-strip`}>
        {photos.map((p) => (
          <button key={p.id} onClick={() => setOpen(p)} className="relative h-16 w-16 rounded-md overflow-hidden border bg-slate-100" data-testid={`${testPrefix}-thumb-${p.id}`}>
            <img src={photoSrc(p)} alt={p.caption || "fotka"} className="h-full w-full object-cover" loading="lazy" onError={(e) => { if (!e.currentTarget.dataset.retried) { e.currentTarget.dataset.retried = "1"; setTimeout(() => { e.currentTarget && (e.currentTarget.src = photoSrc(p) + "?r=" + Date.now()); }, 1500); } }} />
          </button>
        ))}
      </div>
      {open && (
        <Dialog open onOpenChange={() => setOpen(null)}>
          <DialogContent className="max-w-3xl w-[95vw]"><DialogHeader><DialogTitle>{open.caption || "Fotka technika"}</DialogTitle></DialogHeader>
            <img src={photoSrc(open)} alt={open.caption || "fotka"} className="max-h-[70vh] w-full object-contain rounded" data-testid={`${testPrefix}-full`} />
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{open.by} · {open.created_at?.slice(0, 16).replace("T", " ")}</span>
              {canDelete && <Button size="sm" variant="ghost" className="text-red-500" onClick={() => del(open)} data-testid={`${testPrefix}-delete`}><Trash2 className="h-4 w-4 mr-1" /> Smazat</Button>}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

async function compressImage(file, maxSide = 1600, quality = 0.82) {
  if (!file.type.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
    const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    return new File([blob], (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

const uploadErr = (e) => {
  const s = e?.response?.status;
  if (!e?.response || s >= 500) return "Server neodpověděl včas (fotka je příliš velká nebo slabý signál). Zkuste to prosím znovu.";
  return apiErr(e);
};

export function PhotoUpload({ reservationId, onUploaded, compact = false }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, pct: 0 });
  const send = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    setBusy(true);
    let ok = 0; const failed = [];
    for (let i = 0; i < list.length; i++) {
      const f = await compressImage(list[i]);
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max 10 MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      let sent = false;
      for (let attempt = 0; attempt < 3 && !sent; attempt++) {
        try {
          await api.post(`/tech/reservations/${reservationId}/photos`, fd, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
            onUploadProgress: (e) => setProgress({ done: i, total: list.length, pct: Math.round((e.loaded / (e.total || 1)) * 100) }),
          });
          sent = true; ok++;
        } catch (e) {
          if (attempt === 2) failed.push(uploadErr(e));
          else await new Promise((r) => setTimeout(r, 2500 * (attempt + 1)));
        }
      }
    }
    if (ok) toast.success(ok === 1 ? "Fotka nahrána – uvidí ji předseda" : `Nahráno ${ok} fotek – uvidí je předseda`);
    if (failed.length) toast.error(`${failed.length} ${failed.length === 1 ? "fotka se nenahrála" : "fotky se nenahrály"}: ${failed[0]}`);
    if (ok) onUploaded?.();
    setBusy(false); setProgress({ done: 0, total: 0, pct: 0 });
    if (camRef.current) camRef.current.value = "";
    if (galRef.current) galRef.current.value = "";
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => send(e.target.files)} data-testid={`photo-input-${reservationId}`} />
      <input ref={galRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => send(e.target.files)} data-testid={`photo-gallery-input-${reservationId}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => camRef.current?.click()} data-testid={`photo-add-${reservationId}`}>
        {busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> {progress.total > 1 ? `${progress.done + 1}/${progress.total} · ` : ""}{progress.pct}%</> : <><Camera className="h-4 w-4 mr-1" /> Vyfotit</>}
      </Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => galRef.current?.click()} data-testid={`photo-gallery-${reservationId}`}>
        <Images className="h-4 w-4 mr-1" /> {compact ? "Galerie" : "Vybrat z galerie (více fotek)"}
      </Button>
    </div>
  );
}
